/**
 * assignContractor — single canonical write boundary for
 * `Job.assignedSubcontractorId`.
 *
 * Every code path that changes a job's assigned contractor MUST flow through
 * this helper. The helper:
 *   - Computes the diff (prev → next contractorId).
 *   - Emits `contractor_unassigned` ONLY for A→null and A→B
 *     (NOT for null→A and NOT for A→A no-ops).
 *   - Emits `manual_assignment` when source === "manual" and a non-null
 *     contractor is being assigned. (offer_accept does NOT emit this — the
 *     offerEngine already emits `offer_accepted`.)
 *   - Mirrors the legacy display string `assignedSubcontractor`
 *     (companyName) for back-compat.
 *
 * Returns the next jobs array AND the canonical SystemEvents to emit.
 * Pure function — caller owns persistence.
 *
 * Pricing rule: never reads or writes contractValue / payoutAmount /
 * financials.*. Pure assignment metadata only.
 */

import type { Job } from "@/data/types";
import type { SystemEvent } from "@/data/communicationEngine";
import { createSystemEvent } from "@/data/eventFactory";

export type AssignmentSource = "offer_accept" | "manual";

export interface AssignContractorInput {
  jobId: string;
  /** null = unassign. */
  contractorId: string | null;
  source: AssignmentSource;
  actor: string;
  /** Display name for the legacy back-compat string. */
  companyName?: string;
  /** Optional offerId to thread through event payload (for offer_accept). */
  offerId?: string;
  /**
   * Phase 1b — Network Execution: human-readable reason appended to
   * `Job.installerChange[]` for every state-changing transition.
   * Optional for back-compat with existing callers; defaults to
   * "unspecified" when omitted.
   */
  reason?: string;
  /** ISO override for installerChange.at — deterministic tests only. */
  nowIso?: string;
}

export interface AssignContractorResult {
  jobs: Job[];
  events: SystemEvent[];
  /** True when state changed; false for no-op (A→A). */
  changed: boolean;
}

export function assignContractor(
  jobs: Job[],
  input: AssignContractorInput,
): AssignContractorResult {
  const target = jobs.find((j) => j.id === input.jobId);
  if (!target) return { jobs, events: [], changed: false };

  const prev = target.assignedSubcontractorId ?? null;
  const next = input.contractorId;

  // No-op (A→A or null→null).
  if (prev === next) return { jobs, events: [], changed: false };

  const events: SystemEvent[] = [];

  // contractor_unassigned: only A→null and A→B (NOT null→A).
  if (prev !== null) {
    events.push(
      createSystemEvent({
        type: "contractor_unassigned",
        jobId: input.jobId,
        triggeredBy: input.actor,
        payload: {
          previousContractorId: prev,
          nextContractorId: next,
          source: input.source,
          ...(input.offerId ? { offerId: input.offerId } : {}),
        },
      }),
    );
  }

  // manual_assignment: source=manual AND assigning (non-null next).
  if (input.source === "manual" && next !== null) {
    events.push(
      createSystemEvent({
        type: "manual_assignment",
        jobId: input.jobId,
        triggeredBy: input.actor,
        payload: {
          contractorId: next,
          previousContractorId: prev,
        },
      }),
    );
  }

  const at = input.nowIso ?? new Date().toISOString();
  const reason = input.reason ?? "unspecified";

  const nextJobs = jobs.map((j) => {
    if (j.id !== input.jobId) return j;
    const priorChanges = Array.isArray(j.installerChange) ? j.installerChange : [];
    return {
      ...j,
      assignedSubcontractorId: next ?? undefined,
      assignedSubcontractor:
        next === null
          ? undefined
          : (input.companyName ?? j.assignedSubcontractor),
      // Phase 1b — append-only executor swap history. Every state-changing
      // transition (null→A, A→null, A→B) is recorded here.
      installerChange: [
        ...priorChanges,
        {
          fromContractorId: prev,
          toContractorId: next,
          at,
          reason,
          actor: input.actor,
        },
      ],
    } as Job;
  });

  return { jobs: nextJobs, events, changed: true };
}
